/**
 * BANNER AND ERROR BANNER (P36) — reference §7, §11 and §12.
 *
 * "A list-load failure gets a **banner** with *Try again*, not a toast" (§11).
 * The distinction is not severity: a toast is a message ABOUT something that
 * happened, a banner is the state of the thing in front of you. A list that
 * failed to load is not an event — it is the page's condition until somebody
 * retries, and it has to stay on screen saying so.
 *
 * §12's error row names four parts, and `ErrorBanner` asks for three of them:
 * plain words, a **Try again** that re-runs the query, and a **code and a
 * time** so a report to IT names the same failure the log does. Four sites in
 * the app offer one red line and no way out; `onRetry` is optional only because
 * a banner that reports a refusal (not a failure) has nothing to retry.
 *
 * `role="alert"` on the error banner: it appears in place of content the reader
 * was waiting for, so it is announced rather than merely rendered.
 */
import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from './Button';
import type { Tone } from './Badge';

const SURFACES: Record<Tone, string> = {
    neutral: 'border-hair bg-head text-body',
    info: 'border-info-100 bg-info-50 text-info',
    warn: 'border-warn-edge bg-warn-50 text-warn-ink',
    done: 'border-brand-100 bg-brand-50 text-brand-700',
    err: 'border-err-edge bg-err-50 text-err',
};

const GLYPHS: Record<Tone, typeof Info> = {
    neutral: Info, info: Info, warn: AlertTriangle, done: CheckCircle, err: AlertCircle,
};

export interface BannerProps {
    tone?: Tone;
    /** The bold lead sentence. One sentence — §7. */
    title: ReactNode;
    children?: ReactNode;
    action?: ReactNode;
    className?: string;
    role?: 'alert' | 'status';
}

export function Banner({ tone = 'info', title, children, action, className = '', role }: BannerProps) {
    const Glyph = GLYPHS[tone];
    return (
        <div role={role} className={`flex items-start gap-2 rounded-card border px-3 py-2.5 text-sm ${SURFACES[tone]} ${className}`.trim()}>
            <Glyph aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
                <p className="font-semibold">{title}</p>
                {children && <div className="mt-0.5">{children}</div>}
            </div>
            {action && <div className="shrink-0">{action}</div>}
        </div>
    );
}

export interface ErrorBannerProps {
    /** Plain words. What did not happen, not which layer threw. */
    title: ReactNode;
    /** The HTTP status, the error code, whatever names this failure in the log. */
    code?: string | number | null;
    /** When it happened. ISO, per D5. Defaults to now, because a banner is drawn when it fails. */
    at?: string;
    onRetry?: () => void;
    children?: ReactNode;
    className?: string;
}

export function ErrorBanner({ title, code, at, onRetry, children, className = '' }: ErrorBannerProps) {
    const stamp = at ?? new Date().toISOString().slice(0, 19).replace('T', ' ');
    return (
        <Banner
            tone="err"
            role="alert"
            title={title}
            className={className}
            action={onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>}
        >
            {children}
            <p className="mt-0.5 text-xs tabular-nums opacity-90">
                {code !== null && code !== undefined && <span className="font-mono">{code}</span>}
                {code !== null && code !== undefined && ' · '}
                {stamp}
            </p>
        </Banner>
    );
}
